import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Leaf, LogIn, Camera, Sparkles, ShieldCheck, HelpCircle,
  Sun, Droplets, Lightbulb, CheckCircle2, ArrowRight,
  Lock, Info, AlertTriangle, Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useAuth } from '@/contexts/AuthContext';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { TodayTasks } from '@/components/home/TodayTasks';
import { OnboardingHero } from '@/components/home/OnboardingHero';
import { NotificationBanner } from '@/components/home/NotificationBanner';
import { ExploreCards } from '@/components/home/ExploreCards';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/gtag';
import { captureUtmFromLocation, getAnalyticsContext, getStoredUtm } from '@/lib/utm';
import { Brain } from 'lucide-react';
import examplePlant from '@/assets/example-plant.jpg';

// ---------- Helpers ----------
function buildAuthUrl(mode: 'signup' | 'login') {
  const utm = getStoredUtm();
  const params = new URLSearchParams();
  params.set('mode', mode);
  params.set('redirect', '/identify');
  Object.entries(utm).forEach(([k, v]) => v && params.set(k, v));
  return `/auth?${params.toString()}`;
}

// ---------- Landing (unlogged) ----------
export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plantCount, setPlantCount] = useState<number | null>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  const exampleRef = useRef<HTMLElement | null>(null);
  const pricingRef = useRef<HTMLElement | null>(null);
  const seenExample = useRef(false);
  const seenPricing = useRef(false);

  // Capture UTMs + landing_page_view
  useEffect(() => {
    if (user) return;
    captureUtmFromLocation();
    trackEvent('landing_page_view', getAnalyticsContext());
  }, [user]);

  useEffect(() => {
    if (!user) {
      setPlantCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from('plant_identifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (cancelled) return;
      const c = count ?? 0;
      setPlantCount(c);
      trackEvent(c === 0 ? 'home_viewed_first_time' : 'home_viewed_returning', { plant_count: c });
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (user) return;
    const onScroll = () => setShowStickyCta(window.scrollY > 320);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [user]);

  // IntersectionObservers for example_result_view / pricing_view
  useEffect(() => {
    if (user) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.target === exampleRef.current && !seenExample.current) {
          seenExample.current = true;
          trackEvent('example_result_view', getAnalyticsContext());
        }
        if (entry.target === pricingRef.current && !seenPricing.current) {
          seenPricing.current = true;
          trackEvent('pricing_view', getAnalyticsContext());
        }
      });
    }, { threshold: 0.35 });
    if (exampleRef.current) io.observe(exampleRef.current);
    if (pricingRef.current) io.observe(pricingRef.current);
    return () => io.disconnect();
  }, [user]);

  const goSignup = (source: string) => {
    trackEvent('hero_cta_click', { ...getAnalyticsContext(), source });
    navigate(buildAuthUrl('signup'));
  };

  const scrollToExample = () => {
    exampleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---------- LOGGED-IN branch (unchanged) ----------
  if (user) {
    return (
      <div className="min-h-screen bg-background">
        <MobileHeader showLogo />
        <NotificationBanner />
        {plantCount === 0 ? (
          <OnboardingHero />
        ) : (
          <>
            <TodayTasks />
            <section className="px-4 pt-4 pb-2">
              <div className="max-w-lg mx-auto">
                <Button
                  size="lg"
                  onClick={() => navigate('/identify')}
                  className="w-full h-12 gradient-nature text-primary-foreground press-scale touch-target"
                >
                  <Brain className="mr-2 h-5 w-5" />
                  Identificar nova planta
                </Button>
              </div>
            </section>
            <ExploreCards plantCount={plantCount ?? 0} />
          </>
        )}
        <footer className="border-t border-border py-6 pb-24">
          <div className="px-4 text-center text-sm text-muted-foreground">
            <p>© 2025 VerdeIA. Feito com 💚 para amantes de plantas.</p>
          </div>
        </footer>
        <BottomNavigation />
      </div>
    );
  }

  // ---------- LOGGED-OUT redesigned landing ----------
  const problems = [
    'Não sei qual é esta planta',
    'As folhas estão ficando amarelas',
    'Não sei quanto regar',
    'A planta está com manchas ou murchando',
  ];

  const benefits = [
    { icon: Sparkles, title: 'Descobre a espécie provável', desc: 'Nome popular e científico da sua planta.' },
    { icon: Info, title: 'Entende sinais visuais', desc: 'Possíveis causas para folhas amarelas, manchas ou murchamento.' },
    { icon: Droplets, title: 'Sabe como regar e iluminar', desc: 'Orientação de rega e de luz específica para a espécie.' },
    { icon: Leaf, title: 'Salva suas plantas', desc: 'Sua coleção fica guardada para você consultar quando quiser.' },
  ];

  const steps = [
    {
      icon: Lock,
      title: 'Crie sua conta gratuita',
      desc: 'Precisamos identificar você para salvar suas plantas com segurança e você poder consultar depois.',
    },
    {
      icon: Camera,
      title: 'Envie uma foto da planta',
      desc: 'De perto, com boa luz. Pode ser folha, flor ou a planta inteira.',
    },
    {
      icon: Sparkles,
      title: 'Receba a identificação e os cuidados',
      desc: 'Espécie provável, sinais observados, luz, rega e dicas.',
    },
  ];

  const faqs = [
    {
      q: 'Preciso instalar um aplicativo?',
      a: 'Não. A VerdeIA funciona direto no navegador do celular. Se quiser, dá para adicionar à tela inicial como um app.',
    },
    {
      q: 'O plano gratuito exige cartão de crédito?',
      a: 'Não. Você cria sua conta com e-mail ou telefone, sem cadastrar cartão. Nada é cobrado automaticamente.',
    },
    {
      q: 'Quantas identificações gratuitas posso fazer?',
      a: 'Você pode identificar até 3 plantas por mês no plano gratuito. O contador renova a cada mês.',
    },
    {
      q: 'Por que preciso criar uma conta?',
      a: 'A conta guarda sua coleção de plantas e as orientações de cuidados para você consultar depois. Também é o que permite proteger suas fotos com segurança.',
    },
    {
      q: 'A identificação é sempre exata?',
      a: 'Não. A resposta é uma orientação gerada por inteligência artificial e pode se enganar. Em casos graves, procure um profissional (agrônomo, paisagista ou botânico).',
    },
    {
      q: 'Minhas fotos ficam salvas?',
      a: 'Sim, ficam vinculadas apenas à sua conta e são usadas para gerar a análise da sua planta.',
    },
    {
      q: 'Posso usar pelo celular?',
      a: 'Sim. A VerdeIA foi feita para o celular e usa a câmera do próprio aparelho para você tirar a foto na hora.',
    },
    {
      q: 'O que acontece quando atingir o limite gratuito?',
      a: 'Você pode esperar o próximo mês, quando o contador renova, ou assinar o plano Premium por R$ 15/mês para identificar sem limite.',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-[17px] leading-relaxed">
      {/* ---------- Header ---------- */}
      <header className="sticky top-0 z-50 h-14 flex items-center justify-between px-4 bg-background/95 backdrop-blur-sm border-b border-border safe-area-pt">
        <a href="/" className="flex items-center gap-2" aria-label="VerdeIA - início">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <Leaf className="h-4 w-4 text-primary-foreground" aria-hidden />
          </div>
          <span className="font-display font-semibold text-base">VerdeIA</span>
        </a>
        <div className="flex items-center gap-1.5">
          <Button
            onClick={() => navigate(buildAuthUrl('login'))}
            variant="ghost"
            size="sm"
            className="h-10 px-3 press-scale text-base"
          >
            <LogIn className="mr-1.5 h-4 w-4" aria-hidden />
            Entrar
          </Button>
          <Button
            onClick={() => goSignup('header')}
            size="sm"
            className="h-10 px-3 gradient-nature text-primary-foreground press-scale text-base"
          >
            Criar conta
          </Button>
        </div>
      </header>

      <main>
        {/* ---------- Hero ---------- */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 leaf-pattern opacity-20" aria-hidden />
          <div className="px-4 pt-6 pb-8 md:pt-12 md:pb-16 relative">
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 md:gap-10 md:items-center">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                  <Leaf className="h-4 w-4" aria-hidden />
                  Identificação e cuidados de plantas com IA
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 font-display leading-tight">
                  Descubra o que sua planta precisa <span className="text-primary">pela foto</span>
                </h1>

                <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-lg mx-auto md:mx-0">
                  Identifique a espécie, entenda folhas amareladas, manchas ou sinais
                  de falta de água e receba orientações práticas de cuidados.
                </p>

                <div className="flex flex-col gap-3 max-w-md mx-auto md:mx-0">
                  <Button
                    size="lg"
                    onClick={() => goSignup('hero_primary')}
                    className="w-full h-14 text-base font-semibold gradient-nature text-primary-foreground press-scale touch-target"
                  >
                    <Camera className="mr-2 h-5 w-5" aria-hidden />
                    Criar conta grátis e identificar
                  </Button>
                  <p className="text-sm text-muted-foreground flex items-center justify-center md:justify-start gap-2 flex-wrap">
                    <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden />
                    3 identificações grátis por mês
                    <span aria-hidden>·</span>
                    Sem cartão
                    <span aria-hidden>·</span>
                    Cadastro em 1 minuto
                  </p>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={scrollToExample}
                    className="w-full h-12 text-base text-primary hover:text-primary hover:bg-primary/5"
                  >
                    Ver exemplo de resultado
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>

              {/* Hero visual */}
              <div className="mt-8 md:mt-0">
                <HeroPreview src={examplePlant} />
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Situações ---------- */}
        <section className="py-10 md:py-14 px-4 bg-muted/40">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-6 font-display text-center">
              Quando a VerdeIA ajuda
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {problems.map((p) => (
                <li key={p} className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Leaf className="h-3.5 w-3.5 text-primary" aria-hidden />
                  </div>
                  <p className="text-base text-foreground">"{p}"</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------- Demo estática de resultado ---------- */}
        <section ref={exampleRef} id="exemplo" className="py-10 md:py-16 px-4 scroll-mt-16">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <Badge variant="secondary" className="mb-3 text-xs">Exemplo de resultado</Badge>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground font-display">
                Veja o que você recebe depois de enviar uma foto
              </h2>
              <p className="text-base text-muted-foreground mt-2">
                Um exemplo real do que a VerdeIA devolve. Sua planta terá as suas informações.
              </p>
            </div>

            <ExampleResultCard src={examplePlant} />

            <div className="mt-6 text-center">
              <Button
                size="lg"
                onClick={() => goSignup('example_bottom')}
                className="h-14 px-6 text-base font-semibold gradient-nature text-primary-foreground press-scale touch-target"
              >
                Criar minha conta grátis
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden />
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                Sem cartão · 3 identificações grátis por mês
              </p>
            </div>
          </div>
        </section>

        {/* ---------- Como funciona ---------- */}
        <section className="py-10 md:py-16 px-4 bg-muted/40">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-6 font-display text-center">
              Como funciona
            </h2>
            <ol className="space-y-3">
              {steps.map((s, i) => (
                <li key={s.title} className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-border">
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-semibold text-lg">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon className="h-5 w-5 text-primary" aria-hidden />
                      <h3 className="font-semibold text-base sm:text-lg text-foreground">{s.title}</h3>
                    </div>
                    <p className="text-base text-muted-foreground">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ---------- Benefícios ---------- */}
        <section className="py-10 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-6 font-display text-center">
              O que você ganha
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {benefits.map((b) => (
                <div key={b.title} className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-base">{b.title}</h3>
                    <p className="text-base text-muted-foreground">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Plano gratuito ---------- */}
        <section ref={pricingRef} className="py-10 md:py-16 px-4 bg-muted/40">
          <div className="max-w-lg mx-auto">
            <Card className="border-2 border-primary/30 bg-card">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-primary text-primary-foreground">Plano gratuito</Badge>
                  <span className="text-sm text-muted-foreground">Sem cartão</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold font-display text-foreground mb-4">
                  Comece de graça
                </h2>
                <ul className="space-y-3 mb-6">
                  {[
                    '3 identificações grátis todo mês',
                    'Sua coleção salva com segurança',
                    'Orientações de rega e luz',
                    'Sem cartão de crédito',
                    'Sem cobrança automática',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                      <span className="text-base text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-sm text-muted-foreground bg-muted/60 rounded-lg p-3 mb-5">
                  <strong className="text-foreground">E quando eu atingir o limite?</strong>{' '}
                  Você pode esperar o próximo mês (o contador zera) ou assinar o Premium
                  por R$ 15/mês para identificar sem limite. Só se você quiser.
                </div>
                <Button
                  size="lg"
                  onClick={() => goSignup('pricing')}
                  className="w-full h-14 text-base font-semibold gradient-nature text-primary-foreground press-scale touch-target"
                >
                  Criar minha conta gratuita
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ---------- Confiança e transparência ---------- */}
        <section className="py-10 md:py-16 px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-6 font-display text-center">
              Transparência
            </h2>
            <ul className="space-y-3">
              {[
                { icon: ShieldCheck, text: 'Suas fotos são usadas apenas para gerar a análise da planta e ficam vinculadas à sua conta.' },
                { icon: Info, text: 'O resultado é uma orientação gerada por inteligência artificial. Pode se enganar, especialmente em casos raros.' },
                { icon: AlertTriangle, text: 'Casos graves (pragas persistentes, doenças avançadas) merecem também a avaliação de um profissional.' },
                { icon: CheckCircle2, text: 'O plano gratuito não pede cartão de crédito e não há cobrança automática.' },
              ].map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                  <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden />
                  <p className="text-base text-foreground">{text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------- FAQ ---------- */}
        <section className="py-10 md:py-16 px-4 bg-muted/40">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <HelpCircle className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground font-display">
                Perguntas frequentes
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-base sm:text-lg text-foreground hover:text-primary text-left py-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground pb-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ---------- CTA final ---------- */}
        <section className="py-10 md:py-16 px-4">
          <div className="max-w-md mx-auto p-6 sm:p-8 rounded-3xl gradient-nature text-primary-foreground text-center">
            <Leaf className="h-10 w-10 mx-auto mb-3" aria-hidden />
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-display">
              Descubra como cuidar melhor da sua planta
            </h2>
            <p className="mb-6 opacity-95 text-base">
              Crie sua conta gratuita, envie uma foto e receba sua primeira análise.
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => goSignup('final')}
              className="w-full h-14 text-base font-semibold press-scale touch-target"
            >
              Criar conta grátis e identificar
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden />
            </Button>
            <p className="text-sm opacity-90 mt-3">Sem cartão · Cadastro em 1 minuto</p>
          </div>
        </section>
      </main>

      <footer className={`border-t border-border py-6 ${showStickyCta ? 'pb-28 sm:pb-8' : ''}`}>
        <div className="px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 VerdeIA. Feito com 💚 para amantes de plantas.</p>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      {showStickyCta && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 sm:hidden bg-background/95 backdrop-blur-sm border-t border-border px-4 pt-3 animate-fade-in"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
        >
          <Button
            size="lg"
            onClick={() => goSignup('sticky')}
            className="w-full h-14 text-base font-semibold gradient-nature text-primary-foreground press-scale touch-target"
          >
            <Camera className="mr-2 h-5 w-5" aria-hidden />
            Criar conta grátis e identificar
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-1.5">
            Grátis · Sem cartão · Cadastro em 1 minuto
          </p>
        </div>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function HeroPreview({ src }: { src: string }) {
  return (
    <div className="relative mx-auto max-w-sm md:max-w-none">
      <div className="relative rounded-3xl overflow-hidden bg-card border border-border shadow-lg">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={src}
            alt="Exemplo: folha de Monstera com pontos amarelos analisada pela VerdeIA"
            className="w-full h-full object-cover"
            width={1024}
            height={1024}
          />
          <div className="absolute top-3 left-3">
            <Badge className="bg-background/90 text-foreground border border-border text-xs">
              Exemplo
            </Badge>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <h3 className="font-display font-semibold text-lg text-foreground">Costela-de-adão</h3>
            <span className="text-xs text-muted-foreground">Confiança alta</span>
          </div>
          <p className="text-sm italic text-muted-foreground mb-3">Monstera deliciosa</p>
          <div className="grid grid-cols-2 gap-2">
            <MiniStat icon={Sun} label="Luz" value="Meia-sombra" />
            <MiniStat icon={Droplets} label="Rega" value="A cada 7 dias" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }: { icon: typeof Sun; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/60">
      <Icon className="h-4 w-4 text-primary flex-shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium text-foreground truncate">{value}</div>
      </div>
    </div>
  );
}

function ExampleResultCard({ src }: { src: string }) {
  return (
    <Card className="overflow-hidden border-2 border-primary/20">
      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        <img
          src={src}
          alt="Exemplo: folha analisada pela VerdeIA"
          className="w-full h-full object-cover"
          loading="lazy"
          width={1024}
          height={1024}
        />
        <div className="absolute top-3 left-3">
          <Badge className="bg-background/95 text-foreground border border-border">
            Exemplo de resultado da VerdeIA
          </Badge>
        </div>
      </div>

      <CardContent className="p-5 space-y-5">
        <div>
          <h3 className="text-2xl font-bold font-display text-foreground">Costela-de-adão</h3>
          <p className="text-base italic text-muted-foreground">Monstera deliciosa · confiança alta</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <InfoTile icon={Sun} label="Luz solar" value="Meia-sombra, luz indireta abundante" />
          <InfoTile icon={Droplets} label="Rega" value="A cada 7 dias, quando os 2cm superiores estiverem secos" />
        </div>

        <div className="rounded-xl border border-border p-4 bg-muted/40">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="font-semibold text-base text-foreground">Sinais observados na foto</h4>
          </div>
          <ul className="space-y-1.5 text-base text-foreground">
            <li>• Manchas amareladas pontuais na folha — pode indicar excesso de água ou pouca luz.</li>
            <li>• Bordas levemente ressecadas — ambiente com ar seco ou rega irregular.</li>
          </ul>
        </div>

        <div className="rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="font-semibold text-base text-foreground">Cuidado imediato sugerido</h4>
          </div>
          <p className="text-base text-foreground">
            Reduza a frequência de rega, garanta que a terra drene bem e coloque a planta
            em um ponto com mais luz natural indireta durante o dia.
          </p>
        </div>

        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <AlertTriangle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden />
          <p>
            A análise é uma orientação gerada por IA e pode se enganar. Em casos graves,
            complemente com a opinião de um profissional.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoTile({ icon: Icon, label, value }: { icon: typeof Sun; label: string; value: string }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-muted/30">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-primary" aria-hidden />
        <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</span>
      </div>
      <p className="text-base text-foreground">{value}</p>
    </div>
  );
}
